/*
 * Pure helpers for the match lineup: recorded formation geometry, matching
 * match events to lineup players, and comparison rows for team statistics.
 * Nothing here invents data: a player with no recorded grid is not placed on
 * the pitch, and an event whose player cannot be matched confidently is not
 * attached to anyone.
 */

const stripMarks = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "");
export const normName = (s) =>
  stripMarks(s).toLowerCase().replace(/[^a-z\s'-]/g, " ").replace(/\s+/g, " ").trim();

const parts = (name) => normName(name).split(" ").filter(Boolean);
const lastOf = (name) => parts(name).slice(-1)[0] || "";
const initialOf = (name) => (parts(name)[0] || "").charAt(0);

/** "2:4" -> { row: 2, col: 4 }; anything else -> null. */
export function parseGrid(grid) {
  const m = /^(\d+):(\d+)$/.exec(String(grid || "").trim());
  return m ? { row: Number(m[1]), col: Number(m[2]) } : null;
}

/**
 * Groups starters into pitch rows from their recorded grid cell.
 * Returns { placed: boolean, rows: [[entry,...],...] } with rows ordered
 * goalkeeper first. When any starter lacks a grid, `placed` is false and the
 * caller must fall back to a plain list instead of guessing positions.
 *
 * `side` controls horizontal order: columns are recorded from the team's own
 * perspective (col 1 = its left when facing the opponent's goal). With the
 * goalkeeper at the top of the screen the team faces down, so its left is the
 * viewer's right.
 */
export function buildFormationRows(startXI = [], side = "home") {
  const entries = startXI.filter((e) => e?.player);
  if (!entries.length) return { placed: false, rows: [] };
  const parsed = entries.map((e) => ({ entry: e, cell: parseGrid(e.player.grid) }));
  if (parsed.some((p) => !p.cell)) return { placed: false, rows: [] };
  const byRow = new Map();
  parsed.forEach(({ entry, cell }) => {
    if (!byRow.has(cell.row)) byRow.set(cell.row, []);
    byRow.get(cell.row).push({ entry, col: cell.col });
  });
  const rows = [...byRow.keys()]
    .sort((a, b) => a - b)
    .map((r) => {
      const cells = byRow.get(r).sort((a, b) => a.col - b.col);
      const ordered = side === "home" ? cells.reverse() : cells;
      return ordered.map((c) => c.entry);
    });
  return { placed: true, rows };
}

/** Formation string for display; only the recorded one, never inferred. */
export const formationLabel = (team) => (team?.formation ? String(team.formation) : null);

/* ----------------------------- events ------------------------------ */

const typeKey = (e) => String(e?.type || "").toLowerCase();
const detailKey = (e) => String(e?.detail || "").toLowerCase();
export const isGoal = (e) => typeKey(e) === "goal" && !detailKey(e).includes("missed");
export const isOwnGoal = (e) => isGoal(e) && detailKey(e).includes("own");
export const isPenaltyGoal = (e) => isGoal(e) && detailKey(e).includes("penalty");
export const isCard = (e) => typeKey(e) === "card";
export const isSub = (e) => typeKey(e) === "subst";

const minuteOf = (e) => (Number.isFinite(Number(e?.elapsed)) ? Number(e.elapsed) + (Number(e.extra) || 0) : null);
export const eventMinuteLabel = (e) => {
  if (!Number.isFinite(Number(e?.elapsed))) return "";
  return Number(e.extra) ? `${e.elapsed}+${e.extra}'` : `${e.elapsed}'`;
};

/**
 * Finds the lineup player an event name refers to, within one team's squad
 * (starters + substitutes). Event names are sometimes "K. Laimer" and
 * sometimes "Konrad Laimer", so matching goes exact -> surname + initial ->
 * a surname that is unique in the squad. Ambiguity returns null.
 */
export function matchPlayer(name, squad) {
  if (!name) return null;
  const target = normName(name);
  const exact = squad.filter((p) => normName(p.name) === target);
  if (exact.length === 1) return exact[0];
  const last = lastOf(name);
  if (!last) return null;
  const sameLast = squad.filter((p) => lastOf(p.name) === last);
  if (sameLast.length === 1) return sameLast[0];
  const withInitial = sameLast.filter((p) => initialOf(p.name) === initialOf(name));
  return withInitial.length === 1 ? withInitial[0] : null;
}

const blank = () => ({ goals: 0, ownGoals: 0, assists: 0, yellow: 0, red: 0, offAt: null, onAt: null, penalties: 0 });

/**
 * Per-player event summary for one lineup team: { [playerId]: {...} }.
 * Events are limited to the team's own id, so a same-surname player on the
 * opposing side can never be credited.
 */
export function summarisePlayerEvents(team, events = []) {
  const squad = [...(team?.startXI || []), ...(team?.substitutes || [])].map((e) => e.player).filter(Boolean);
  const teamId = Number(team?.team?.id);
  const out = {};
  squad.forEach((p) => {
    out[p.id] = blank();
  });
  const mine = events.filter((e) => !Number.isFinite(teamId) || !e.team_id || Number(e.team_id) === teamId);
  mine.forEach((e) => {
    if (isGoal(e)) {
      const scorer = matchPlayer(e.player_name, squad);
      if (scorer) {
        if (isOwnGoal(e)) out[scorer.id].ownGoals += 1;
        else {
          out[scorer.id].goals += 1;
          if (isPenaltyGoal(e)) out[scorer.id].penalties += 1;
        }
      }
      if (!isOwnGoal(e)) {
        const assister = matchPlayer(e.assist_name, squad);
        if (assister) out[assister.id].assists += 1;
      }
    } else if (isCard(e)) {
      const p = matchPlayer(e.player_name, squad);
      if (!p) return;
      const d = detailKey(e);
      if (d.includes("red") || d.includes("second")) out[p.id].red += 1;
      else if (d.includes("yellow")) out[p.id].yellow += 1;
    } else if (isSub(e)) {
      // Provider convention: player_name leaves, assist_name enters.
      const off = matchPlayer(e.player_name, squad);
      const on = matchPlayer(e.assist_name, squad);
      if (off) out[off.id].offAt = minuteOf(e);
      if (on) out[on.id].onAt = minuteOf(e);
    }
  });
  return out;
}

/** Goals for a team from events, for header goalscorer lines. */
export function goalLines(events = [], teamId) {
  return events
    .filter((e) => isGoal(e) && Number(e.team_id) === Number(teamId))
    .map((e) => ({
      name: e.player_name || "Goal",
      minute: eventMinuteLabel(e),
      own: isOwnGoal(e),
      pen: isPenaltyGoal(e),
    }));
}

/* -------------------------- team statistics ------------------------- */

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace("%", ""));
  return Number.isFinite(n) ? n : null;
};

const statMap = (team) => new Map((team?.statistics || []).map((s) => [s.type, s.value]));

/** Rows spec: possession first, then paired comparison rows. Missing -> null (never 0). */
export const STAT_ROWS = [
  { key: "expected_goals", label: "Expected goals (xG)", digits: 2 },
  { key: "Total Shots", label: "Total shots" },
  { key: "Shots on Goal", label: "Shots on target" },
  { key: "Shots insidebox", label: "Shots inside box" },
  { key: "Corner Kicks", label: "Corners" },
  { key: "Total passes", label: "Passes" },
  { key: "Passes %", label: "Pass accuracy", pct: true },
  { key: "Fouls", label: "Fouls" },
  { key: "Offsides", label: "Offsides" },
  { key: "Yellow Cards", label: "Yellow cards" },
  { key: "Red Cards", label: "Red cards" },
  { key: "Goalkeeper Saves", label: "Saves" },
];

export function buildStatComparison(homeTeam, awayTeam) {
  const h = statMap(homeTeam);
  const a = statMap(awayTeam);
  const possession = { home: num(h.get("Ball Possession")), away: num(a.get("Ball Possession")) };
  const rows = STAT_ROWS.map((r) => ({ ...r, home: num(h.get(r.key)), away: num(a.get(r.key)) })).filter(
    (r) => r.home !== null || r.away !== null
  );
  const hasPossession = possession.home !== null && possession.away !== null;
  return { possession: hasPossession ? possession : null, rows };
}

/** Highest-stakes rows shown on Overview; everything is on the Stats tab. */
export const OVERVIEW_KEYS = ["expected_goals", "Total Shots", "Shots on Goal", "Corner Kicks"];

/** Share of the pair for a bar; null when both sides are missing/zero. */
export function barShares(home, away) {
  const h = home ?? 0;
  const a = away ?? 0;
  const t = h + a;
  if (home === null && away === null) return null;
  if (!(t > 0)) return { home: 0.5, away: 0.5, empty: true };
  return { home: h / t, away: a / t, empty: false };
}
